/**
 * m3 echo Frontend - Quota query keys
 *
 * One place naming every cached answer that a booking invalidates, because
 * they were being invalidated one at a time and the list kept coming up short.
 *
 * The booking preview is the one that hurt: it answers "does this fit", and a
 * cached "yes" that outlives the booking which used the room up is worse than
 * no preview at all - the bars beside it refetch, so the panel then shows a
 * full month next to a sentence saying the configuration fits, and the save
 * fails with a 400 nobody was warned about. Anything that books, releases or
 * corrects quota therefore invalidates all three views together, through this
 * function, rather than each call site remembering the list.
 */

import type { QueryClient } from '@tanstack/react-query';

/**
 * The customer-facing quota (`GET /autopilots/quota`). Tenant-scoped in the
 * full key; the prefix matches every tenant's entry.
 */
export const AUTOPILOT_QUOTA_KEY = ['autopilotQuota'] as const;

/** The admin view of one tenant's quota (`GET /admin/tenants/{id}/autopilot-quota`). */
export const TENANT_QUOTA_KEY = ['tenantAutopilotQuota'] as const;

/** The costed configuration (`POST /autopilots/quota/preview`). */
export const BOOKING_PREVIEW_KEY = ['autopilotBookingPreview'] as const;

/**
 * The full preview key. Exported so the dialog's `useQuery` and its
 * revalidation before saving cannot drift apart: a `fetchQuery` under a key
 * that differs by so much as the tenant slot would leave the shown verdict
 * untouched and check a different one.
 *
 * The tenant is in the key, not just in the request, because a create books
 * against the *project's* tenant - a price cached for one tenant must never be
 * shown against another's bars.
 */
export const bookingPreviewKey = (
  projectId: number,
  tenantId: number | undefined,
  request: unknown
) => [...BOOKING_PREVIEW_KEY, projectId, tenantId ?? null, request];

interface InvalidateOptions {
  /**
   * Whether a *mounted and enabled* preview observer should re-ask right now,
   * or merely be marked stale for its next use. Default `'active'`.
   *
   * `'none'` is for the caller that is about to close the dialog: the only
   * observer of that key is a heartbeat away from being disabled, so refetching
   * it buys an answer nobody will read - one wasted round trip on every single
   * create. Marking it stale is enough, on two independent grounds:
   * `invalidateQueries` flags the entry as invalidated, and a later mount
   * refetches an invalidated entry regardless of how fresh it looks -
   * `staleTime` never enters that decision - and the preview key embeds the
   * dialog's request, which changes when the dialog closes and again when it
   * reopens, so the next dialog mounts its key anew either way.
   */
  previewRefetch?: 'active' | 'none';
}

/**
 * Everything whose answer changes when quota is booked, released or corrected.
 * Deliberately keyed by prefix and not narrowed to one tenant or project: a
 * stale entry that is merely refetched costs one request, a stale entry that
 * is kept costs a rejected save.
 */
export const invalidateQuotaViews = (
  queryClient: QueryClient,
  options?: InvalidateOptions
): void => {
  queryClient.invalidateQueries({ queryKey: AUTOPILOT_QUOTA_KEY });
  queryClient.invalidateQueries({ queryKey: TENANT_QUOTA_KEY });
  queryClient.invalidateQueries({
    queryKey: BOOKING_PREVIEW_KEY,
    refetchType: options?.previewRefetch ?? 'active',
  });
};
